import { HeroSection } from "@/containers/HeroSection";
import Toolbar from "@/containers/Toolbar";
import { BottomSheet } from "@/design-system/BottomSheet/BottomSheet";
import { Button } from "@/design-system/Button/Button";
import { EmptyPlayerCard } from "@/design-system/PlayerCard/PlayerCard";
import {
  RoleOption,
  RoleSelectPanel,
} from "@/design-system/RoleSelectPanel/RoleSelectPanel";
import MinimizeButton from "@/design-system/SpecificButtons/MinimizeButton/MinimizeButton";
import { arrow } from "@/designSystemIcons";
import { CompareSubsection, trackViewContent } from "@/events/compareMixpanel";
import { useGoBack } from "@/hooks/useGoBack";
import { findScrollContainer } from "@/hooks/useSticky";
import { cn } from "@/lib/utils";
import { topToPageSelector } from "@/redux/globalState/globalStateSlice";
import { BREAKPOINTS } from "@/settings";
import { Trans, useTranslation } from "@repo/shared/i18n";
import { comparePlayerKey } from "@repo/shared/redux";
import { Maximize2, Minimize2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

import { AddPlayerSearch } from "./AddPlayerSearch";
import { ColumnDividers } from "./ColumnDividers";
import { ComparePlayerCard } from "./ComparePlayerCard";
import { CompareRoleNudge } from "./CompareRoleNudge";
import { CompareSections, useOpenSections } from "./CompareSections";
import {
  CompareStickyPlayersRow,
  useCompareStickyHeader,
} from "./CompareStickyPlayer";
import { ModeToggle, useCompareMode } from "./ModeToggle";
import { COLUMN_WIDTH, columnGridStyle } from "./compareLayout";
import { AddPlayerButton, MobileComparison } from "./mobile/CompareMobile";
import { useCompareData } from "./useCompareData";

type ComparePageProps = {
  /**
   * Rendered inside the CompareWidget bottom sheet rather than as the routed
   * page: no URL ↔ slice sync, no hero chrome — instead a title row with
   * expand/collapse and close buttons (top right).
   */
  embedded?: boolean;
  /** Embedded only: whether the sheet is expanded to full screen. */
  expanded?: boolean;
  /** Embedded only: toggles the sheet between 90dvh and full screen. */
  onToggleExpand?: () => void;
  /** Embedded only: closes the sheet (players stay staged). */
  onClose?: () => void;
  /**
   * Embedded only: controlled search-phase state, lifted to the widget so a
   * backdrop click can dismiss the search before closing the sheet.
   */
  searching?: boolean;
  onSearchingChange?: (searching: boolean) => void;
};

const ComparePage = ({
  embedded = false,
  expanded = false,
  onToggleExpand,
  onClose,
  searching,
  onSearchingChange,
}: ComparePageProps) => {
  // Opened from any page, so it has no route parent; shared comparisons land
  // on the players list.
  const goBack = useGoBack({ fallback: "/players" });
  const { t } = useTranslation("compare");
  const {
    players,
    slots,
    sectionTiers,
    categoryWinners,
    metaRatingWinners,
    playerRole,
    setPlayerRole,
    playerRoleLabel,
    playerRoles,
    roleGroups,
    roleMismatch,
    removePlayer,
    setSlotChemistry,
    setSlotChemStyleId,
  } = useCompareData({ syncUrl: !embedded });

  // Anchored panel on desktop (also inside the compare widget's sheet — it's
  // absolutely positioned, so the sheet's transform doesn't trap it); bottom
  // sheet only on touch devices.
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);
  const rolePickerPresentation = isTouchDevice ? "sheet" : "panel";

  // One role picker, used by the desktop toolbar and the mobile toolbar.
  const roleSelect = (
    <RoleSelectPanel
      triggerLabel={playerRole ? playerRoleLabel : t("role.bestRole")}
      groups={roleGroups}
      selectedRoleId={playerRole?.id}
      onSelect={(role?: RoleOption) =>
        setPlayerRole(
          role ? playerRoles.find((pr) => pr.id === role.id) : undefined,
        )
      }
      subtitle={
        <Trans
          ns="compare"
          i18nKey="role.subtitle"
          components={{ hl: <span className="text-neutral-300" /> }}
        />
      }
      presentation={rolePickerPresentation}
    />
  );

  const hasPlayers = players.length > 0;

  const compareSubsection: CompareSubsection = embedded ? "Sheet" : "Page";

  // view_content once per open (the sheet unmounts when closed, so a reopen is
  // a fresh mount — expanding it isn't, which is right: same comparison). Waits
  // for the first non-empty set: a routed cold load mounts with an empty slice
  // while the URL ids are still being fetched, and a 0-player view is noise. A
  // genuinely empty /compare has no comparison to report and never fires.
  const viewTracked = useRef(false);
  useEffect(() => {
    if (viewTracked.current || players.length === 0) return;
    viewTracked.current = true;
    trackViewContent({
      subsection: compareSubsection,
      player_count: players.length,
    });
  }, [players.length, compareSubsection]);

  const [mode, handleModeChange] = useCompareMode({
    subsection: compareSubsection,
    playerCount: players.length,
  });
  // Embedded only: the add-slot swaps the sheet content for the search view.
  // Controlled by the widget when provided (backdrop-dismiss layering).
  const [internalSearching, setInternalSearching] = useState(false);
  const isSearching = searching ?? internalSearching;
  const setIsSearching = onSearchingChange ?? setInternalSearching;

  // After adding from the search view, land back at the top of the
  // comparison. The vertical scroller differs per host: embedded it's the
  // sheet's content wrapper (the search view replaces the comparison inside
  // the same scroller, which keeps its offset — window.scrollTo can't reach
  // it), routed it's the page itself.
  const embeddedRootRef = useRef<HTMLDivElement>(null);
  const scrollComparisonToTop = () => {
    const container = embeddedRootRef.current
      ? findScrollContainer(embeddedRootRef.current)
      : null;
    if (container) container.scrollTo({ top: 0 });
    else window.scrollTo({ top: 0 });
  };
  const addPlayerSearch = (
    <AddPlayerSearch
      onAdded={() => {
        setIsSearching(false);
        scrollComparisonToTop();
      }}
      onCancel={() => setIsSearching(false)}
      playerRoleId={playerRole?.id}
      subsection={compareSubsection}
    />
  );
  const { openSections, toggleSection } = useOpenSections();

  // The card rail gets one extra trailing column for the persistent "add
  // player" slot; the sections below span only the actual players (both on
  // the same 280px column axis).
  const cardGridStyle = columnGridStyle(players.length + 1, COLUMN_WIDTH);
  const gridStyle = columnGridStyle(players.length, COLUMN_WIDTH);

  // Sticky compressed header: flips once the cards have scrolled up to the
  // navbar (sentinel bottom within the 48px navbar height of the viewport
  // top). Embedded, it's measured against the sheet's scroll container
  // instead and pins to its top.
  const topToPage = useSelector(topToPageSelector);
  const { scrolledPast, sentinelRef, stickyClassName } = useCompareStickyHeader(
    {
      offset: embedded ? 0 : 48,
      withinScrollContainer: embedded,
      topClassName: embedded ? "top-0" : topToPage.tailwindString,
    },
  );

  const content = (
    <>
      {/* Player cards + trailing add-slot — left-aligned on the content rail.
          w-full keeps the rail from shrink-centering when it's a flex item
          (embedded wrapper is a flex column; mx-auto would otherwise win). */}
      <div className="mx-auto w-full max-w-content px-4 pt-2 large:px-16 touch-device:px-4">
        <div className="relative w-fit pb-4">
          <div className="grid" style={cardGridStyle}>
            {players.map((player, idx) => (
              <ComparePlayerCard
                key={comparePlayerKey(player)}
                player={player}
                slot={slots[idx]}
                variant="desktop"
                isMetaRatingWinner={metaRatingWinners[idx]}
                gradedRoleId={playerRole?.id}
                onRemove={removePlayer}
                setSlotChemistry={setSlotChemistry}
                setSlotChemStyleId={setSlotChemStyleId}
              />
            ))}
            <div className="flex items-center justify-center">
              <EmptyPlayerCard onClick={() => setIsSearching(true)} />
            </div>
          </div>
          <ColumnDividers count={players.length + 1} />
        </div>
      </div>

      {hasPlayers && (
        <>
          {/* Sentinel: flips `scrolledPast` when the toolbar meets the navbar
              (page) / the sheet's scroll-container top (embedded). */}
          <div ref={sentinelRef} className="h-0" />

          {/* Full-bleed controls toolbar. Once scrolled past the cards it sticks
              under the navbar (or at the sheet top) and shows the compressed
              players. */}
          <div className={stickyClassName}>
            <Toolbar color="bg-neutral-800">
              <div className="flex w-full items-center justify-between gap-x-6">
                {!scrolledPast && (
                  <CompareRoleNudge
                    roleMismatch={roleMismatch}
                    onSelectRole={setPlayerRole}
                  />
                )}
                <div className="flex items-center gap-x-6">
                  {/* Compressed players keep the 280px column grid; the
                      toggle on their left shifts them off the exact card
                      axis while stuck. */}
                  {scrolledPast && (
                    <CompareStickyPlayersRow
                      players={players}
                      gridStyle={gridStyle}
                      gradedRoleId={playerRole?.id}
                    />
                  )}
                </div>
                {!scrolledPast && (
                  <div className="flex items-center gap-x-4">
                    {roleSelect}
                    <ModeToggle mode={mode} onChange={handleModeChange} />
                  </div>
                )}
              </div>
            </Toolbar>
          </div>

          {/* PlayStyles + Attributes — same left-aligned column axis as cards */}
          <div className="mx-auto w-full max-w-content px-4 pb-[30px] large:px-16 touch-device:px-4">
            <div
              className={cn(
                "flex flex-col gap-y-4 relative w-fit",
                mode !== "compact" && "pt-4",
              )}
            >
              <CompareSections
                players={players}
                gridStyle={gridStyle}
                sectionTiers={sectionTiers}
                categoryWinners={categoryWinners}
                mode={mode}
                openSections={openSections}
                toggleSection={toggleSection}
              />
            </div>
          </div>
        </>
      )}
    </>
  );

  // Touch devices swap the fixed-280px desktop rails for the mobile column
  // system (min-170px columns, one shared horizontal scroller, pinned
  // headers). Vertical scrolling stays with the host (page / sheet wrapper),
  // so the global ad padding sits at the end of the scrolled flow. No
  // trailing add slot — adding happens from a "+" in the host's header.
  const mobileContent = (
    <MobileComparison
      players={players}
      sectionTiers={sectionTiers}
      categoryWinners={categoryWinners}
      metaRatingWinners={metaRatingWinners}
      removePlayer={removePlayer}
      setSlotChemistry={setSlotChemistry}
      setSlotChemStyleId={setSlotChemStyleId}
      gradedRoleId={playerRole?.id}
      roleTrigger={roleSelect}
      roleMismatch={roleMismatch}
      onSelectRole={setPlayerRole}
      subsection={compareSubsection}
      // Routed: the page body scrolls, so the sticky header pins under the
      // navbar. Embedded (default): the sheet's scroll-container top.
      stickyTop={
        embedded
          ? undefined
          : {
              topClassName: topToPage.tailwindString,
              offset: topToPage.value,
              withinScrollContainer: false,
            }
      }
    />
  );

  if (embedded) {
    return (
      // The sheet's content wrapper scrolls; min-h-full just fills it when
      // the comparison is shorter than the sheet.
      <div
        ref={embeddedRootRef}
        className="flex min-h-full w-full grow flex-col"
      >
        {!isTouchDevice && (
          <div className="mx-auto flex w-full max-w-content items-center justify-between px-4 py-4 large:px-16 touch-device:px-4">
            <div className="flex items-center gap-x-3">
              <h2 className="text-base font-semibold">{t("title")}</h2>
            </div>
            <div className="flex items-center gap-x-2">
              {
                <>
                  <Button
                    variant="secondary"
                    size="small"
                    icon={expanded ? <Minimize2 /> : <Maximize2 />}
                    onClick={onToggleExpand}
                    aria-label={
                      expanded ? t("header.collapse") : t("header.expand")
                    }
                  />
                  <Button
                    variant="secondary"
                    size="small"
                    icon={<div className="size-3">{arrow}</div>}
                    onClick={onClose}
                    aria-label={t("header.close")}
                  />
                </>
              }
            </div>
          </div>
        )}
        {isSearching
          ? addPlayerSearch
          : isTouchDevice
            ? mobileContent
            : content}
      </div>
    );
  }

  return (
    <HeroSection
      helmetTitle={t("title")}
      helmetDescription={t("metaDescription")}
      back={goBack}
      shouldBeInTheDocumentFlow
    >
      {isTouchDevice ? (
        <>
          <div className="flex items-center justify-end px-4 py-2">
            <AddPlayerButton onClick={() => setIsSearching(true)} />
          </div>
          {mobileContent}
          <BottomSheet
            isOpen={isSearching}
            onClose={() => setIsSearching(false)}
            className="h-[85dvh] bg-neutral-900"
          >
            {addPlayerSearch}
          </BottomSheet>
        </>
      ) : isSearching ? (
        <div className="mx-auto w-full max-w-content">{addPlayerSearch}</div>
      ) : (
        content
      )}
    </HeroSection>
  );
};

export default ComparePage;
